import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import SettingsHubPanel from '../../components/SettingsHubPanel';
import TextInput from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import { AccordionItem } from '../../components/ui/Accordion';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import {
  useSystemEmailTemplates,
  updateSystemEmailTemplate,
  type SystemEmailTemplateListItem,
} from './useData';
import { cn } from '../../lib/utils';
import Card from '../../components/ui/Card';
import type { EmbeddedInConfigurationHub } from '../configuration-settings/models';

const SYSTEM_EMAIL_TEMPLATE_EXAMPLES: Record<string, { subject: string; bodyHtml: string }> = {
  user_invitation: {
    subject: "You're invited to join",
    bodyHtml:
      '<p>You have been invited to create an account.</p>\n' +
      '<p>Click the link below to complete your registration. This link expires in {{expiryDays}} days.</p>\n' +
      '<p><a href="{{registerLink}}">{{registerLink}}</a></p>\n' +
      '<p>Best regards,<br />{{appName}}</p>',
  },
  password_reset: {
    subject: 'Reset your password',
    bodyHtml:
      '<p>Hello {{userName}},</p>\n' +
      '<p>You requested a password reset. Click the link below to set a new password:</p>\n' +
      '<p><a href="{{resetLink}}">{{resetLink}}</a></p>\n' +
      '<p>Best regards,<br />{{appName}}</p>',
  },
  task_notification: {
    subject: 'Task notification – {{appName}}',
    bodyHtml:
      '<br /><br />' +
      '<div style="font-family: Arial, sans-serif; max-width: 640px; margin: 0 auto; color: #333;">\n' +
      '  <div style="border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">\n' +
      '    <div style="background: #1e3a5f; color: #fff; padding: 12px 16px; font-weight: bold; font-size: 16px;">TECHNICAL SUPPORT / TASK NOTIFICATION</div>\n' +
      '    <div style="padding: 16px; background: #f9fafb;">\n' +
      '      <p style="margin: 0 0 12px 0; font-size: 13px;">Dear {{clientName}},<br />Please find below Task completed at {{taskEndDate}} ({{taskDuration}}).</p>\n' +
      '      <p style="margin: 0 0 12px 0; font-size: 13px;"><strong>TASK DETAILS</strong></p>\n' +
      '      <table style="width: 100%; border-collapse: collapse; font-size: 13px;">\n' +
      '        <tr><td style="padding: 6px 0; border-bottom: 1px solid #eee;"><strong>Client:</strong></td><td style="padding: 6px 0; border-bottom: 1px solid #eee;">{{clientName}}</td></tr>\n' +
      '        <tr><td style="padding: 6px 0; border-bottom: 1px solid #eee;"><strong>Assigned to:</strong></td><td style="padding: 6px 0; border-bottom: 1px solid #eee;">{{assignedToName}}</td></tr>\n' +
      '        <tr><td style="padding: 6px 0; border-bottom: 1px solid #eee;"><strong>Date:</strong></td><td style="padding: 6px 0; border-bottom: 1px solid #eee;">{{taskDate}}</td></tr>\n' +
      '        <tr><td style="padding: 6px 0; border-bottom: 1px solid #eee;"><strong>Date Start:</strong></td><td style="padding: 6px 0; border-bottom: 1px solid #eee;">{{taskStartDate}}</td></tr>\n' +
      '        <tr><td style="padding: 6px 0; border-bottom: 1px solid #eee;"><strong>Date End:</strong></td><td style="padding: 6px 0; border-bottom: 1px solid #eee;">{{taskEndDate}}</td></tr>\n' +
      '        <tr><td style="padding: 6px 0; border-bottom: 1px solid #eee;"><strong>Duration:</strong></td><td style="padding: 6px 0; border-bottom: 1px solid #eee;">{{taskDuration}}</td></tr>\n' +
      '        <tr><td style="padding: 6px 0; border-bottom: 1px solid #eee;"><strong>Status:</strong></td><td style="padding: 6px 0; border-bottom: 1px solid #eee;">{{taskStatus}}</td></tr>\n' +
      '      </table>\n' +
      '      <p style="margin: 14px 0 6px 0; font-size: 13px;"><strong>DESCRIPTION / NOTES:</strong></p>\n' +
      '      <p style="margin: 0; padding: 10px; background: #fff; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 13px; line-height: 1.5;">{{taskDescription}}</p>\n' +
      '      <p style="margin: 16px 0 0 0; font-size: 12px; color: #6b7280;">Best regards,<br /><strong>{{appName}}</strong></p>\n' +
      '      {{signature}}\n' +
      '    </div>\n' +
      '  </div>\n' +
      '</div>',
  },
  email_signature: {
    subject: '',
    bodyHtml:
      '<table style="width: 438px; font-size: 10pt; font-family: Arial, sans-serif; margin-top: 24px;" border="0" cellspacing="0" cellpadding="0"><tbody><tr><td style="font-size: 10pt; font-family: Arial, sans-serif; border-right: 3px solid #2196f3; width: 177px; padding-right: 10px; vertical-align: top;" valign="top"><p><img style="height: 48px; width: 152px; display: block; margin-left: auto; margin-right: auto;" src="http://unibyte.gr/signatures/Unibyte_signature_logo.png" alt="Logo" width="120" /></p><h2 style="margin-top: 32px; margin-bottom: 0; line-height: 1; text-align: center;"><strong><span style="font-size: 12pt; font-family: Arial, sans-serif; color: #2196f3;">George Vlachavas</span></strong></h2></td><td style="width: 249px;" valign="top"><table style="width: 249px;" border="0" cellspacing="0" cellpadding="0"><tbody><tr><td style="font-size: 10pt; font-family: Arial, sans-serif; padding-bottom: 5px; padding-left: 30px; vertical-align: top; line-height: 1.3;"><strong>M</strong> +30 6932 399 938<br /><strong>P</strong> +30 210 94 84 370<br /><strong>E</strong> info@unibyte.gr</td></tr><tr><td style="font-size: 10pt; font-family: Arial, sans-serif; padding: 5px 0 5px 30px; vertical-align: top; line-height: 1.3;"><strong>UNIBYTE</strong><br />&#917;&#956;&#956;. &#922;&#945;&#956;&#960;&#959;&#965;&#961;&#942; 34, 183 45, &#913;&#952;&#942;&#957;&#945;</td></tr><tr><td style="font-size: 10pt; font-family: Arial, sans-serif; padding: 5px 0 5px 30px; vertical-align: top;"><a style="text-decoration: none; color: #2196f3;" href="http://www.unibyte.gr" target="_blank" rel="noopener"><strong>www.unibyte.gr</strong></a></td></tr></tbody></table></td></tr><tr><td style="font-size: 8pt; font-family: Arial, sans-serif; padding-right: 10px; vertical-align: top;" colspan="2"><span style="color: #000;">The company accepts no liability for the content of this email, or for the consequences of any actions taken on the basis of the information provided, unless that information is subsequently confirmed in writing.</span></td></tr></tbody></table><hr /><p>&nbsp;</p>',
  },
};

export type SystemEmailTemplatesPageProps = EmbeddedInConfigurationHub;

const SystemEmailTemplatesPage: React.FC<SystemEmailTemplatesPageProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const isAdmin = user?.role === 'ADMIN';

  const {
    templates: systemEmailTemplates,
    globalPlaceholders: systemEmailGlobalPlaceholders,
    isLoading: systemTemplatesLoading,
    mutate: mutateSystemTemplates,
  } = useSystemEmailTemplates();

  const [editingTemplate, setEditingTemplate] = useState<SystemEmailTemplateListItem | null>(null);
  const [templateForm, setTemplateForm] = useState({ subject: '', bodyHtml: '', isEnabled: true });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const handleSaveSystemEmailTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      await updateSystemEmailTemplate(editingTemplate.key, {
        subject: templateForm.subject,
        bodyHtml: templateForm.bodyHtml || undefined,
        isEnabled: templateForm.isEnabled,
      });
      await mutateSystemTemplates();
      setEditingTemplate(null);
      showSuccess('Template saved');
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  if (!isAdmin) {
    if (embedded) {
      return (
        <Card variant="outlined" className="p-6 text-center">
          <p className={cn('text-sm', colors.text)}>System email templates are only available to administrators.</p>
        </Card>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className={cn('mb-2 text-2xl font-bold', colors.text)}>Access denied</h2>
          <p className={cn('text-sm', colors.textMuted)}>This page is only available to administrators.</p>
        </div>
      </div>
    );
  }

  if (systemTemplatesLoading) {
    if (embedded) {
      return (
        <div className="py-10">
          <LoadingSpinner message="Loading templates…" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Loading templates…" size="large" />
      </div>
    );
  }

  const templateBody = (
    <>
      <div className={cn('rounded-lg border p-3', colors.border, colors.surface)}>
        <p className={cn('text-xs font-medium mb-1.5', colors.text)}>Global (all templates)</p>
        <ul className={cn('text-xs space-y-0.5', colors.textMuted)}>
          {systemEmailGlobalPlaceholders.map((g) => (
            <li key={g.placeholder}>
              <code className="bg-black/5 dark:bg-white/10 px-1 rounded">{g.placeholder}</code> — {g.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="space-y-3">
        {systemEmailTemplates.map((t) => (
          <div
            key={t.key}
            className={cn('flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border', colors.border, colors.surface)}
          >
            <div className="min-w-0">
              <p className={cn('font-medium text-sm', colors.text)}>
                {t.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </p>
              <p className={cn('text-xs mt-0.5', colors.textMuted)}>{t.description}</p>
              <p className={cn('text-xs mt-1', colors.textMuted)}>
                This template: {t.placeholders.length ? t.placeholders.join(', ') : '—'}
              </p>
              <span
                className={cn(
                  'mt-1 inline-block rounded px-1.5 py-0.5 text-xs font-medium',
                  (t.isEnabled ?? true)
                    ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
                    : cn('bg-black/[0.04] dark:bg-white/[0.06]', colors.textMuted),
                )}
              >
                {(t.isEnabled ?? true) ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingTemplate(t);
                setTemplateForm({
                  subject: t.subject,
                  bodyHtml:
                    t.bodyHtml ?? (t.key === 'email_signature' ? (SYSTEM_EMAIL_TEMPLATE_EXAMPLES.email_signature?.bodyHtml ?? '') : ''),
                  isEnabled: t.isEnabled ?? true,
                });
              }}
            >
              Edit
            </Button>
          </div>
        ))}
      </div>
    </>
  );

  const editModal = (
      <Modal isOpen={!!editingTemplate} onClose={() => setEditingTemplate(null)} size="lg">
        {editingTemplate && (
          <>
            <ModalHeader>
              <ModalTitle>
                Edit: {editingTemplate.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </ModalTitle>
            </ModalHeader>
            <ModalContent>
              <p className={cn('text-sm mb-3', colors.textMuted)}>{editingTemplate.description}</p>
              <div className="flex items-center gap-2 mb-3">
                <Switch
                  id="template-enabled"
                  checked={templateForm.isEnabled}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, isEnabled: e.target.checked }))}
                />
                <Label htmlFor="template-enabled" className={cn('text-sm', colors.text)}>
                  Enabled
                </Label>
              </div>
              <div className={cn('mb-3 p-3 rounded border', colors.border, colors.surface)}>
                <p className={cn('text-xs font-medium mb-1.5', colors.text)}>Global (all templates)</p>
                <ul className={cn('text-xs', colors.textMuted)}>
                  {systemEmailGlobalPlaceholders.map((g) => (
                    <li key={g.placeholder}>
                      <code className="bg-black/5 dark:bg-white/10 px-1 rounded">{g.placeholder}</code> — {g.label}
                    </li>
                  ))}
                </ul>
              </div>
              <p className={cn('text-xs mb-2', colors.textMuted)}>
                This template: {editingTemplate.placeholders.length ? editingTemplate.placeholders.join(', ') : '—'}
              </p>
              {SYSTEM_EMAIL_TEMPLATE_EXAMPLES[editingTemplate.key] && (
                <AccordionItem title="Example (with placeholders)" defaultOpen={false} className="mb-4">
                  <div className={cn('p-3 rounded border bg-black/5 dark:bg-white/5', colors.border)}>
                    <p className={cn('text-xs mb-1', colors.text)}>
                      <span className="font-semibold">Subject:</span> {SYSTEM_EMAIL_TEMPLATE_EXAMPLES[editingTemplate.key].subject}
                    </p>
                    <p className={cn('text-xs mb-1', colors.text)}>Body HTML:</p>
                    <pre
                      className={cn(
                        'text-xs whitespace-pre-wrap break-words rounded px-2 py-1 font-mono',
                        'bg-black/10 dark:bg-white/10',
                        colors.text
                      )}
                    >
                      {SYSTEM_EMAIL_TEMPLATE_EXAMPLES[editingTemplate.key].bodyHtml}
                    </pre>
                  </div>
                </AccordionItem>
              )}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="template-subject">Subject</Label>
                  <TextInput
                    id="template-subject"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, subject: e.target.value }))}
                    placeholder="Email subject"
                    className="mt-1 w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="template-body">HTML body</Label>
                  <Textarea
                    id="template-body"
                    value={templateForm.bodyHtml}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, bodyHtml: e.target.value }))}
                    placeholder="<p>HTML content. Use placeholders above.</p>"
                    rows={12}
                    className={cn('mt-1 w-full font-mono text-sm', colors.background, colors.text, colors.border)}
                  />
                </div>
              </div>
            </ModalContent>
            <ModalFooter>
              <Button type="button" variant="outline" onClick={() => setEditingTemplate(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={() => void handleSaveSystemEmailTemplate()}
                loading={savingTemplate}
                disabled={savingTemplate}
              >
                Save
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
  );

  if (embedded) {
    return (
      <>
        <SettingsHubPanel
          title="System email templates"
          description="Customize subject and HTML for transactional emails. Use placeholders as documented below."
          helpItems={[
            {
              summary: 'SMTP delivery',
              content: (
                <p>
                  Outbound mail uses the server configured under{' '}
                  <strong className={cn('font-medium', colors.text)}>Configuration → Email notifications</strong>.
                </p>
              ),
            },
          ]}
          contentClassName="space-y-4"
        >
          {templateBody}
        </SettingsHubPanel>
        {editModal}
      </>
    );
  }

  return (
    <>
      <div className="h-full overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-6 p-6">
          <div>
            <h1 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>System email templates</h1>
            <p className={cn('mt-1 text-sm', colors.textMuted)}>
              Subjects and HTML bodies for invitations, password reset, task notifications, and signatures.
            </p>
          </div>
          <p className={cn('text-xs', colors.textMuted)}>
            Customize subject and HTML body for transactional emails. Use placeholders as documented below.
          </p>
          {templateBody}
        </div>
      </div>
      {editModal}
    </>
  );
};

export default SystemEmailTemplatesPage;
